import { ReactNode, useCallback, useEffect, useState } from 'react';
import { createContext } from 'react';

import { CurrencyTypeEnum } from '../model/currency_type.enum';
import { DataLabelEnum } from '../model/data_label.enum';
import { RangeEnum } from '../model/range.enum';
import Utils from '../utils/immersive_utils';

import useLayout, { KEYS } from './layout-hook';
import usePersistentState from './use-persistent-state';

const LAYOUT_KEY = 'OVERVIEW_LAYOUT';
const DEFAULT_LAYOUTS = {
  [KEYS.LG]: [
    { i: 'market-history', x: 0, y: 0, w: 6, h: 4 },
    { i: 'snapshot-data', x: 6, y: 0, w: 6, h: 4 },
    { i: 'token-config', x: 0, y: 0, w: 12, h: 0.4 },
  ],
  [KEYS.MD]: [
    { i: 'market-history', x: 0, y: 0, w: 6, h: 4 },
    { i: 'snapshot-data', x: 8, y: 0, w: 6, h: 4 },
    { i: 'token-config', x: 0, y: 0, w: 12, h: 0.4 },
  ],
  [KEYS.SM]: [
    { i: 'market-history', x: 0, y: 0, w: 12, h: 4 },
    { i: 'snapshot-data', x: 8, y: 0, w: 12, h: 4 },
    { i: 'token-config', x: 0, y: 0, w: 12, h: 0.4 },
  ],
  [KEYS.XS]: [
    { i: 'market-history', x: 0, y: 0, w: 12, h: 4 },
    { i: 'snapshot-data', x: 0, y: 0, w: 12, h: 4 },
    { i: 'token-config', x: 0, y: 0, w: 12, h: 0.4 },
  ],
  [KEYS.XXS]: [
    { i: 'market-history', x: 0, y: 0, w: 12, h: 4 },
    { i: 'snapshot-data', x: 0, y: 0, w: 12, h: 4 },
    { i: 'token-config', x: 0, y: 0, w: 12, h: 0.4 },
  ],
};

interface IOverviewParamsProvider {
  children?: ReactNode;
  currency?: CurrencyTypeEnum;
  providerKey?: string;
}

interface IOverviewProvider {
  layouts: any;
  maximizeCard: (card: string) => void;
  setLayouts: (props: any) => void;
  maximized: string;
  setMaximized: (props: string) => void;
  currency: CurrencyTypeEnum;
  setCurrency: (props: CurrencyTypeEnum) => void;
  label: DataLabelEnum;
  setLabel: (props: DataLabelEnum) => void;
  currentTimeStamp: string;
  setTimeStamp: (props: string) => void;
  initTimeStamp: string;
  setInitTimeStamp: (props: string) => void;
  resetLayout: () => void;
  changeLayoutHeight: (card: string, values: { size: string; value: number }[]) => void;
  showVolSurfaceSmile: boolean;
  setShowVolSurfaceSmile: (value: boolean) => void;
  rowExpanded: Set<string>;
  toggleRowExpand: (card: string) => void;
}

export const OverviewContext = createContext({} as IOverviewProvider);

export const OverviewProvider = (props: IOverviewParamsProvider) => {
  const STATE_PATH = [
    props.providerKey ? `TAB_${props.providerKey}` : 'OVERVIEW',
    props?.currency || props.providerKey || '',
  ];

  const [currency, setCurrency] = usePersistentState<CurrencyTypeEnum>(
    STATE_PATH,
    'currency',
    CurrencyTypeEnum.BITCOIN,
  );

  const [label, setLabel] = useState<DataLabelEnum>(DataLabelEnum.PERPETUAL);
  const [currentTimeStamp, setTimeStamp] = useState<string>(new Date().toISOString());
  const [initTimeStamp, setInitTimeStamp] = useState<string>(
    Utils.getInitDateByRange(new Date(), RangeEnum.D1).toISOString(),
  );

  const layoutKey = props.providerKey
    ? `${LAYOUT_KEY}_${currency}_${props.providerKey}`
    : `${LAYOUT_KEY}_${currency}`;

  useEffect(() => {
    if (props?.currency) {
      setCurrency(props.currency);
    }
  }, [props]);

  const {
    setLayouts,
    layouts,
    maximized,
    setMaximized,
    changeLayoutHeight,
    showVolSurfaceSmile,
    setShowVolSurfaceSmile,
  } = useLayout(layoutKey, DEFAULT_LAYOUTS);

  const [rowExpanded, setRowExpanded] = useState<Set<string>>(new Set());
  const [baselineLayouts, setBaselineLayouts] = useState<any | null>(null);

  useEffect(() => {
    setLayouts((prev: any) => {
      let needsClean = false;
      const cleaned: any = {};
      Object.keys(prev).forEach((bp) => {
        cleaned[bp] = (prev[bp] || []).map((item: any) => {
          if (item.static) {
            needsClean = true;
            return { ...item, static: false };
          }
          return item;
        });
      });
      return needsClean ? cleaned : prev;
    });
  }, [setLayouts]);

  const toggleRowExpand = (card: string) => {
    const next = new Set(rowExpanded);
    if (next.has(card)) next.delete(card);
    else next.add(card);

    if (rowExpanded.size === 0 && next.size > 0 && !baselineLayouts) {
      setBaselineLayouts(layouts);
    }

    if (next.size === 0) {
      if (baselineLayouts) {
        const restored: any = JSON.parse(JSON.stringify(baselineLayouts));
        Object.keys(restored).forEach((bp) => {
          restored[bp] = (restored[bp] || []).map((item: any) => ({ ...item, static: false }));
        });
        setLayouts(restored);
      }
      setBaselineLayouts(null);
      setRowExpanded(next);
      return;
    }

    const COLS_BY_KEY: Record<string, number> = { lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 };
    const source = baselineLayouts || layouts;
    const recomputed: any = JSON.parse(JSON.stringify(source));

    Object.keys(recomputed).forEach((bp) => {
      const cols = COLS_BY_KEY[bp] ?? COLS_BY_KEY.lg;
      recomputed[bp] = recomputed[bp].map((item: any) =>
        next.has(item.i) ? { ...item, x: 0, w: cols, static: true } : { ...item, static: false },
      );
    });

    setRowExpanded(next);
    setLayouts(recomputed);
  };

  const resetOverviewLayout = useCallback(() => {
    setRowExpanded(new Set());
    setBaselineLayouts(null);
    setMaximized('');
    setLayouts(DEFAULT_LAYOUTS);
  }, [setLayouts, setMaximized]);

  return (
    <OverviewContext.Provider
      value={{
        setLayouts,
        layouts,
        maximizeCard: toggleRowExpand,
        maximized,
        setMaximized,
        currency,
        setCurrency,
        label,
        setLabel,
        currentTimeStamp,
        setTimeStamp,
        initTimeStamp,
        setInitTimeStamp,
        resetLayout: resetOverviewLayout,
        changeLayoutHeight,
        showVolSurfaceSmile,
        setShowVolSurfaceSmile,
        rowExpanded,
        toggleRowExpand,
      }}
    >
      {props.children}
    </OverviewContext.Provider>
  );
};
